import {
  BackgammonAdapter,
  type BackgammonDice,
  type BackgammonPosition,
  type BackgammonTurn,
} from '@chessmate/game-engine';

/**
 * The robot's turn pick, factored out of the store so it can run in a web
 * worker (deep searches are synchronous CPU work; on the main thread they
 * freeze the kid's screen). Pure by construction: a fresh adapter with the
 * default Math.random roller is fine here because `chooseTurn` never rolls —
 * the caller rolls and passes the dice in.
 *
 * Plain-object in and out, so the same function serves the worker protocol
 * (structured clone) and the store's no-worker fallback.
 */
export interface RobotTurnRequest {
  readonly position: BackgammonPosition;
  readonly dice: BackgammonDice;
  /** undefined = Easy (greedy, 'noisy': a random pick among the better half of
   *  the legal turns); 1 = exact depth-1 expectimax (Clever); 2 = bounded
   *  depth-2 probe (Genius). */
  readonly depth?: 1 | 2;
}

/**
 * Easy's randomness, seeded from the request itself (FNV-1a over the position
 * and roll, then mulberry32). A given position and roll always draw the same
 * "noise", so audit goldens, scripted-dice flows and tests stay reproducible
 * and a stale-reply re-pick agrees with the first — while still varying from
 * position to position, which is all the variety a kid can see.
 */
export function robotRng(request: RobotTurnRequest): () => number {
  let h = 0x811c9dc5;
  const text = JSON.stringify([request.position, request.dice]);
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  let state = h >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export function pickRobotTurn(request: RobotTurnRequest): BackgammonTurn {
  const adapter = new BackgammonAdapter();
  const game = adapter.fromPosition(request.position, request.dice);
  if (request.depth) return adapter.chooseTurn(game, { depth: request.depth });
  // Easy: pure greed ties on most moves and used to play by point order, a
  // different (and, as black, worse-than-random) style per colour. 'noisy' over
  // the colour-blind ordering is balanced between the seats and gentler.
  return adapter.chooseTurn(game, { mood: 'noisy', rng: robotRng(request) });
}
